import { withStore } from 'common/src/vtj/mobx/action-creator'
import fp from 'lodash/fp'
import { toJS } from 'mobx'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { normalizeTextInputWhitespace } from 'todistukset-common/src/vtj/form-sanitization.util'
import {
  validateContactInfoEmail,
  validateContactInfoPhoneNumber,
  validateIncludeApostille,
  validateIncludeHetu,
  validateIncludeMaritalStatus,
  validateIncludePreviousPermanentAddresses,
  validateIncludePreviousMarriages,
  validateIncludePreviousNames,
  validateIncludeTranslationForm,
  validateMailDeliveryCountry,
  validateMailDeliveryFirstName,
  validateMailDeliveryLastName,
  validateMailDeliveryPostalCode,
  validateMailDeliveryPostOffice,
  validateMailDeliveryStreetAddress,
  validateMailDeliveryStreetAddressExtras,
  validateOrdererExtraIdentificationBirthCountry,
  validateOrdererExtraIdentificationBirthPlace,
  validateOrdererExtraIdentificationFinnishHetu,
  validateOrdererExtraIdentificationForeignPersonId,
  validateOrdererExtraIdentificationForeignPersonIdCountry,
  validateOrdererExtraIdentificationPreviousName,
  validatePersons,
  validatePreviousAddressesOverYears,
  validateTodistusLanguage,
  validateTranslationFormLanguage,
  validateUsageRegionCountry,
  validateUsageRegionReceiver,
  validateUsageRegionTarget,
  ValidationError,
  ValueValidatorCommon,
} from 'todistukset-common/src/vtj/form-validation.util'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import { TodistuksetUi } from 'todistukset-ui/src/vtj/common-types'
import { propertyNameTranslationKeysAetAndUaetForms } from 'todistukset-common/src/vtj/validation'
import { getStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { checkIfTranslationIsAvailable } from 'todistukset-ui/src/vtj/translation.util'
import { TodistusStoreForm } from 'todistukset-common/src/vtj/types/form-types.type'
import {
  finalEditableFieldValue,
  isEditableField,
} from 'todistukset-common/src/vtj/types/editable-field.type'

const UNKNOWN_VALIDATION_PROPERTY = 'unknownProperty'
export const UNKNOWN_VALIDATION_ERROR_TRANSLATION_KEY = 'validationUnknownError'

type Validation = [ValueValidatorCommon<TodistusStoreForm>, string]
type Validator = (validations: Validation[]) => ValidationError[]

export const getValidationErrorFromValidator = (
  validation: ValueValidatorCommon<TodistusStoreForm>,
  value: unknown,
  form: TodistusStoreForm,
  propertyName?: string
): ValidationError | undefined => {
  const store = getStore()
  const user = store.common.loggedInUser.get()
  const result = validation(value, form, user)

  if (result === undefined) {
    return undefined
  }

  if (typeof result === 'boolean') {
    return !result
      ? {
          propertyName: propertyName ?? UNKNOWN_VALIDATION_PROPERTY,
          errorTranslationKey: UNKNOWN_VALIDATION_ERROR_TRANSLATION_KEY,
        }
      : undefined
  }

  return result
}

const getValidator = (certificateType: CertificateTypes): Validator =>
  withStore((store: TodistuksetUi.Store) => {
    const form = store.forms[certificateType]
    return (validations: Validation[]): ValidationError[] => {
      const validationResults = fp.compact(
        validations.map(
          ([validate, propertyName]): ValidationError | undefined => {
            const value = toJS(fp.get(propertyName, form))
            return getValidationErrorFromValidator(
              validate,
              isEditableField(value) ? finalEditableFieldValue(value) : value,
              form,
              propertyName
            )
          }
        )
      )
      return validationResults
    }
  })

export const validateBasicInformationFields = ({
  certificateType,
}: {
  certificateType: CertificateTypes
}): ValidationError[] => {
  const validate = getValidator(certificateType)
  const validations: Validation[] = [
    [
      validatePreviousAddressesOverYears,
      'previousAddressesOverYears.ilmoitettuArvo',
    ],
    [validateIncludeHetu, 'includeHetu.ilmoitettuArvo'],
    [validateIncludePreviousNames, 'includePreviousNames.ilmoitettuArvo'],
    [validateIncludeMaritalStatus, 'includeMaritalStatus.ilmoitettuArvo'],
    [
      validateIncludePreviousPermanentAddresses,
      'includePreviousPermanentAddresses.ilmoitettuArvo',
    ],
    [
      validateIncludePreviousMarriages,
      'includePreviousMarriages.ilmoitettuArvo',
    ],
    [validatePersons, 'persons'],
  ]

  return validate(validations)
}

export const validateUsageRegionFields = ({
  certificateType,
}: {
  certificateType: CertificateTypes
}): ValidationError[] => {
  const validate = getValidator(certificateType)
  const validations: Validation[] = [
    [validateUsageRegionTarget, 'usageRegion.target'],
    [validateUsageRegionCountry, 'usageRegion.countryKoodistoCode'],
    [validateUsageRegionReceiver, 'usageRegion.receiver'],
  ]
  return validate(validations)
}

export const validateLanguageFields = ({
  certificateType,
}: {
  certificateType: CertificateTypes
}): ValidationError[] =>
  withStore((store: TodistuksetUi.Store) => {
    const form = store.forms[certificateType]
    const { usageRegion } = form
    const isTranslationEnabled = checkIfTranslationIsAvailable({ usageRegion })
    const validate = getValidator(certificateType)
    const validations: Validation[] = [
      [validateTodistusLanguage, 'todistusLanguage.ilmoitettuArvo'],
    ]
    const apostilleValidations: Validation[] = [
      [validateIncludeApostille, 'includeApostille.ilmoitettuArvo'],
    ]
    const translationValidations: Validation[] = [
      [
        validateTranslationFormLanguage,
        'translationFormLanguage.ilmoitettuArvo',
      ],
      [validateIncludeTranslationForm, 'includeTranslationForm.ilmoitettuArvo'],
    ]

    return isTranslationEnabled
      ? validate([
          ...validations,
          ...apostilleValidations,
          ...translationValidations,
        ])
      : validate([...validations, ...apostilleValidations])
  })

export const validateDeliveryMethodFields = ({
  certificateType,
}: {
  certificateType: CertificateTypes
}): ValidationError[] => {
  const validate = getValidator(certificateType)
  const validations: Validation[] = [
    [validateMailDeliveryFirstName, 'mailDelivery.firstName'],
    [validateMailDeliveryLastName, 'mailDelivery.lastName'],
    [validateMailDeliveryStreetAddress, 'mailDelivery.streetAddress'],
    [
      validateMailDeliveryStreetAddressExtras,
      'mailDelivery.streetAddressExtras',
    ],
    [validateMailDeliveryPostOffice, 'mailDelivery.postOffice'],
    [validateMailDeliveryPostalCode, 'mailDelivery.postalCode'],
    [validateMailDeliveryCountry, 'mailDelivery.country'],
  ]
  return validate(validations)
}

export const validateContactInfoFields = ({
  certificateType,
}: {
  certificateType: CertificateTypes
}): ValidationError[] => {
  const validate = getValidator(certificateType)
  const validations: Validation[] = [
    [validateContactInfoPhoneNumber, 'contactInfo.phoneNumber.ilmoitettuArvo'],
    [validateContactInfoEmail, 'contactInfo.email.ilmoitettuArvo'],
  ]
  return validate(validations)
}

export function normalizeWhitespace(
  fn: ValueValidatorCommon<TodistusStoreForm>
): ValueValidatorCommon<TodistusStoreForm> {
  return ((value: unknown, form: TodistusStoreForm, user: TodistuksetUser) => {
    const sanitized = normalizeTextInputWhitespace(value)
    return fn(sanitized || null, form, user)
  }) as ValueValidatorCommon<TodistusStoreForm>
}

export const validateOrdererExtraIdentificationFields = ({
  certificateType,
}: {
  certificateType: CertificateTypes
}): ValidationError[] => {
  const validate = getValidator(certificateType)
  const validations: Validation[] = [
    [
      normalizeWhitespace(validateOrdererExtraIdentificationBirthPlace),
      'ordererExtraIdentification.birthPlace',
    ],
    [
      normalizeWhitespace(validateOrdererExtraIdentificationBirthCountry),
      'ordererExtraIdentification.birthCountry',
    ],
    [
      normalizeWhitespace(validateOrdererExtraIdentificationFinnishHetu),
      'ordererExtraIdentification.finnishHetu',
    ],
    [
      normalizeWhitespace(validateOrdererExtraIdentificationForeignPersonId),
      'ordererExtraIdentification.foreignPersonId',
    ],
    [
      validateOrdererExtraIdentificationForeignPersonIdCountry,
      'ordererExtraIdentification.foreignPersonIdCountry',
    ],
    [
      normalizeWhitespace(validateOrdererExtraIdentificationPreviousName),
      'ordererExtraIdentification.previousName',
    ],
  ]
  return validate(validations)
}

const propertyNameTranslationKeys: { [k: string]: string } = {
  ...propertyNameTranslationKeysAetAndUaetForms,
  'contactInfo.email': 'orderWizardDeliveryMethodStepInputLabel_contactEmail',
  'contactInfo.phoneNumber':
    'orderWizardDeliveryMethodStepInputLabel_contactPhoneNumber',
  'mailDelivery.streetAddress':
    'orderWizardDeliveryMethodStepInputLabel_streetAddress',
  'mailDelivery.streetAddressExtras':
    'orderWizardDeliveryMethodStepInputLabel_streetAddressExtras',
  'mailDelivery.postalCode':
    'orderWizardDeliveryMethodStepInputLabel_postalCode',
  'mailDelivery.postOffice':
    'orderWizardDeliveryMethodStepInputLabel_postOffice',
  'mailDelivery.country': 'orderWizardDeliveryMethodStepInputLabel_country',
  persons: 'validationSubjectMissing',
  todistusLanguage: 'orderWizardAdditonalSelectionsStepLanguageSelectionTitle',
  translationFormLanguage:
    'orderWizardAdditonalSelectionsStepTranslationSelectionLanguageSelectLabel',
  'usageRegion.target': 'orderWizardUsageRegionStepRegionSelectWidgetHeading',
  'usageRegion.countryKoodistoCode':
    'orderWizardUsageRegionStepCountrySelectWidgetHeading',
  'usageRegion.receiver':
    'orderWizardUsageRegionStepReceiverTypeSelectWidgetHeading',
  'ordererExtraIdentification.birthPlace':
    'orderWizardPersonalInformationStepInputLabel_birthPlace',
  'ordererExtraIdentification.birthCountry':
    'orderWizardPersonalInformationStepInputLabel_birthCountry',
  'ordererExtraIdentification.finnishHetu':
    'orderWizardPersonalInformationStepInputLabel_finnishHetu',
  'ordererExtraIdentification.foreignPersonId':
    'orderWizardPersonalInformationStepInputLabel_foreignPersonId',
  'ordererExtraIdentification.foreignPersonIdCountry':
    'orderWizardPersonalInformationStepInputLabel_foreignPersonIdCountry',
  'ordererExtraIdentification.previousName':
    'orderWizardPersonalInformationStepInputLabel_previousName',
}

export const getPropertyTranslationKey = (err: ValidationError): string =>
  err.propertyName in propertyNameTranslationKeys
    ? propertyNameTranslationKeys[err.propertyName]
    : err.propertyName
